import type {
  TimelineLayer,
  Keyframe,
  Tween,
  EasingType,
} from '../types';
import type { TimelineLayerResponse } from '../types/api';

const DEFAULT_KEYFRAME_STATE = {
  x: 0,
  y: 0,
  width: 100,
  height: 100,
  rotation: 0,
  opacity: 1,
};

function normalizeSize(value: number | undefined): number {
  return typeof value === 'number' && value > 0 ? value : 100;
}

function parseStateProperties(properties?: string): Record<string, number> | null {
  if (!properties) return null;
  try {
    return JSON.parse(properties) as Record<string, number>;
  } catch {
    return null;
  }
}

/** Timeline / stage display order: lower order = higher in the list and further forward on stage. */
export function sortTimelineLayers(layers: TimelineLayer[]): TimelineLayer[] {
  return [...layers].sort((a, b) => a.order - b.order);
}

/**
 * Converts TimelineLayerResponse[] from API to timeline store format.
 * Populates layers, keyframes, and tweens from the backend response.
 */
export function convertLayersResponseToTimelineData(
  layersResponse: TimelineLayerResponse[]
): {
  layers: TimelineLayer[];
  keyframes: Record<string, Keyframe[]>;
  tweens: Record<string, Tween[]>;
} {
  const sortedLayers = [...layersResponse].sort((a, b) => a.sorter - b.sorter);
  const layers: TimelineLayer[] = sortedLayers.map((layer, index) => ({
    id: String(layer.id),
    objectId: layer.sceneObjects.length > 0 ? String(layer.sceneObjects[0].id) : '',
    name: layer.name,
    order: index,
    visible: true,
    locked: false,
  }));

  const keyframes: Record<string, Keyframe[]> = {};
  const keyframeIdToFrame = new Map<number, number>();

  for (const layer of layersResponse) {
    const layerId = String(layer.id);
    const primarySceneObject = layer.sceneObjects?.[0];
    const primaryDefaultProps = parseStateProperties(primarySceneObject?.defaultState?.properties);
    const fallbackState = {
      x: primaryDefaultProps?.left ?? primaryDefaultProps?.x ?? DEFAULT_KEYFRAME_STATE.x,
      y: primaryDefaultProps?.top ?? primaryDefaultProps?.y ?? DEFAULT_KEYFRAME_STATE.y,
      width: normalizeSize(
        primaryDefaultProps?.width ?? primaryDefaultProps?.w ?? primarySceneObject?.width
      ),
      height: normalizeSize(
        primaryDefaultProps?.height ?? primaryDefaultProps?.h ?? primarySceneObject?.height
      ),
      rotation: primaryDefaultProps?.rotation ?? DEFAULT_KEYFRAME_STATE.rotation,
      opacity: primaryDefaultProps?.opacity ?? DEFAULT_KEYFRAME_STATE.opacity,
    };

    keyframes[layerId] = (layer.keyFrames ?? []).map((kf) => {
      keyframeIdToFrame.set(kf.id, kf.frameNumber);

      const objectStates: Record<string, { x: number; y: number; width: number; height: number; rotation: number; opacity: number }> = {};

      for (const sceneObj of layer.sceneObjects ?? []) {
        const defaultProps = parseStateProperties(sceneObj.defaultState?.properties);
        const objFallback = {
          x: defaultProps?.left ?? defaultProps?.x ?? DEFAULT_KEYFRAME_STATE.x,
          y: defaultProps?.top ?? defaultProps?.y ?? DEFAULT_KEYFRAME_STATE.y,
          width: normalizeSize(defaultProps?.width ?? defaultProps?.w ?? sceneObj.width),
          height: normalizeSize(defaultProps?.height ?? defaultProps?.h ?? sceneObj.height),
          rotation: defaultProps?.rotation ?? DEFAULT_KEYFRAME_STATE.rotation,
          opacity: defaultProps?.opacity ?? DEFAULT_KEYFRAME_STATE.opacity,
        };
        const stateForObject = kf.objectStates?.find(
          (objState) => objState.sceneObjectId === sceneObj.id
        );
        if (stateForObject?.properties) {
          try {
            const props = JSON.parse(stateForObject.properties) as Record<string, number>;
            objectStates[String(sceneObj.id)] = {
              x: props.left ?? props.x ?? objFallback.x,
              y: props.top ?? props.y ?? objFallback.y,
              width: normalizeSize(props.width ?? objFallback.width),
              height: normalizeSize(props.height ?? objFallback.height),
              rotation: props.rotation ?? objFallback.rotation,
              opacity: props.opacity ?? objFallback.opacity,
            };
          } catch {
            objectStates[String(sceneObj.id)] = objFallback;
          }
        } else {
          objectStates[String(sceneObj.id)] = objFallback;
        }
      }

      const primaryState = primarySceneObject
        ? objectStates[String(primarySceneObject.id)] ?? fallbackState
        : fallbackState;

      return {
        id: String(kf.id),
        layerId,
        frame: kf.frameNumber,
        state: primaryState,
        objectStates: Object.keys(objectStates).length > 0 ? objectStates : undefined,
      };
    });
  }

  const tweens: Record<string, Tween[]> = {};
  for (const layer of layersResponse) {
    const layerId = String(layer.id);
    tweens[layerId] = (layer.tweens ?? []).map((tw) => {
      const startFrame = keyframeIdToFrame.get(tw.startKeyFrameId) ?? 0;
      const endFrame = keyframeIdToFrame.get(tw.endKeyFrameId) ?? startFrame + 1;
      return {
        id: String(tw.id),
        layerId,
        startFrame,
        endFrame,
        easing: (tw.easing as EasingType) || 'linear',
      };
    });
  }

  return { layers, keyframes, tweens };
}

/** En yüksek keyframe kare numarası; hiç keyframe yoksa null. */
export function getLastKeyframeFrame(keyframes: Record<string, Keyframe[]>): number | null {
  let max = -1;
  for (const list of Object.values(keyframes)) {
    for (const kf of list) {
      if (kf.frame > max) max = kf.frame;
    }
  }
  return max < 0 ? null : max;
}
